import React, { useEffect, useRef, useState } from 'react';
import Arrivals from './arrivals';
import ArrivalsDeparturesSwitcher from './arrivalsDeparturesSwitcher';
import Departures from './departures';
import { ArrivalsDataItem, DeparturesDataItem, ShipsDataItem } from './interfaces/arrivalsDepartures.interfaces';
import ShipVisitHistory from './shipVisitHistory';
import { CustomProvider, DateRangePicker } from 'rsuite';
import sv from 'rsuite/locales/sv_SE';
import en from 'rsuite/locales/en_GB';
import { useTranslation } from 'react-i18next';

export interface ArrivalsDeparturesProps {
  show: boolean;
}

const ArrivalsDepartures = ({ show }: ArrivalsDeparturesProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [locale, setLocale] = useState<string>('en');
  const [showArrivals, setShowArrivals] = useState(true);
  const [showDepartures, setShowDepartures] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [selectedShip, setSelectedShip] = useState<ShipsDataItem | null>(null);
  const [selectedImo, setSelectedImo] = useState<string | null>(null);
  const [fallbackImage, setFallbackImage] = useState<string | null>(null);
  const [dateSpan, setDateSpan] = useState<Date[]>();

  const { t } = useTranslation();

  useEffect(() => {
    const e = document.getElementById('arrivalsDepartures');
    if (e?.dataset.pageLocale) {
      setLocale(e.dataset.pageLocale);
    }

    if (e?.dataset.fallbackImage && e?.dataset.fallbackImage.length > 0) {
      setFallbackImage(e.dataset.fallbackImage);
    }
  }, []);

  const switchMode = (arrivals: boolean) => {
    if (arrivals) {
      setShowArrivals(true);
      setShowDepartures(false);
      setShowHistory(false);
    } else {
      setShowArrivals(false);
      setShowDepartures(true);
      setShowHistory(false);
    }
  };

  const showShipHistory = (shipDetails: ShipsDataItem, imo: string) => {
    setSelectedShip(shipDetails);
    setSelectedImo(imo);
    setShowHistory(true);
  };

  const closeHistory = () => {
    setSelectedShip(null);
    setSelectedImo(null);
    setShowHistory(false);
  };

  const changeDateSpan = (e: any) => {
    setDateSpan(e);
  };

  return (
    <div ref={ref} className="arrivals-departures">
      {(showArrivals || showDepartures) && !showHistory ? (
        <ArrivalsDeparturesSwitcher
          onChange={(arrivals: boolean) => switchMode(arrivals)}
          showArrivals={showArrivals}
        />
      ) : null}

      {!showHistory ? (
        <div className="arrivals-departures-top-bar">
          <CustomProvider locale={locale === 'sv' ? sv : en}>
            <DateRangePicker
              appearance="default"
              placeholder={t('arrivalsdepartures.date')}
              onChange={(e) => {
                changeDateSpan(e);
              }}
              isoWeek
            />
          </CustomProvider>
        </div>
      ) : null}

      <Arrivals
        show={showArrivals && !showHistory}
        refresh={showArrivals}
        locale={locale}
        fallbackImage={fallbackImage}
        dateSpan={dateSpan}
        onShowHistory={(selectedShip: ShipsDataItem, data: ArrivalsDataItem) => showShipHistory(selectedShip, data.imo)}
      />
      <Departures
        show={showDepartures && !showHistory}
        refresh={showDepartures}
        locale={locale}
        fallbackImage={fallbackImage}
        dateSpan={dateSpan}
        onShowHistory={(selectedShip: ShipsDataItem, data: DeparturesDataItem) =>
          showShipHistory(selectedShip, data.imo)
        }
      />
      <ShipVisitHistory
        show={showHistory}
        selectedShip={selectedShip}
        imo={selectedImo}
        locale={locale}
        onGoBack={closeHistory}
      />
    </div>
  );
};

export default ArrivalsDepartures;
